import { useId } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

import { GLYPH_PATHS, blazonFor, pipStarPoints } from "./rank-blazon-glyphs"

/**
 * OKS-512 — Rank blazon patch (shield + tier color + Lucide glyph +
 * climbing star pips). One of the two foundational identity primitives
 * consumed by the profile (§11), leaderboard (§13), and feed (§10).
 *
 * # Pure presentational
 *
 * Takes a ladder POSITION (not a name) plus the brand's tier count, and
 * the rank's REAL configured `name` (the design's Rookie/Rising/etc are
 * illustrative only — UX Spec §1). No data fetch, no brand axis; brand
 * isolation is the host's responsibility (PRD §6).
 *
 * # One viewBox, uniform scaling
 *
 * Shield, glyph, and pips all live in a single `0 0 72 84` viewBox so
 * the whole blazon scales crisply from `lg` (standalone) to `sm`
 * (avatar corner). The glyph `d` data is canonical lucide-static (see
 * `rank-blazon-glyphs.ts`) — never hand-crafted.
 *
 * # Accessibility (UX Spec §5)
 *
 * Rank conveyed by `name` via `role="img"` + `aria-label="Rank: {name}"`
 * — never color/glyph alone.
 *
 * # Locked DOM contract (LQE arc/variant assertions)
 *
 *   - root `<svg data-testid="rank-patch">` with `data-tier`,
 *     `data-tier-count`, `data-glyph`, `data-pips`
 *   - each pip is `data-testid="rank-patch-pip"`
 *   - caption (showLabel only): `data-testid="rank-patch-label"`
 */
export interface RankPatchProps {
  /** 1-based ladder position (1 = floor). Clamped into `1..tierCount`. */
  tier: number
  /** Total tiers in this brand's ladder. Defaults to today's 4. */
  tierCount?: number
  /** The rank's real configured name — label + `aria-label`. */
  name: string
  /** `sm` = avatar corner, `md` = inline, `lg` = standalone. */
  size?: "sm" | "md" | "lg"
  /** Render the name caption under the shield (standalone only). */
  showLabel?: boolean
  className?: string
}

const SIZE_PX: Record<NonNullable<RankPatchProps["size"]>, number> = {
  sm: 28,
  md: 48,
  lg: 72,
}

// viewBox geometry (72 × 84).
const VB_W = 72
const VB_H = 84
const ASPECT = VB_H / VB_W

// Heraldic rounded shield: rounded top corners, straight flanks, then a
// soft curve to the bottom point.
const SHIELD_PATH =
  "M8 11 Q8 6 13 6 H59 Q64 6 64 11 V44 Q64 64 36 78 Q8 64 8 44 Z"

export function RankPatch({
  tier,
  tierCount = 4,
  name,
  size = "md",
  showLabel = false,
  className,
}: RankPatchProps) {
  const { t } = useTranslation()
  const gradientId = useId()

  const blazon = blazonFor(tier, tierCount)
  const px = SIZE_PX[size]
  const ariaLabel = t("rank.patch.aria_label", { name })

  // Pips drop at corner size (UX Spec §4) — glyph + color carry identity
  // there. `data-pips` always reflects the logical count regardless.
  const showPips = size !== "sm"
  const pipCenterY = 66
  const pipSpacing = 9
  const pipStartX = VB_W / 2 - ((blazon.pips - 1) * pipSpacing) / 2

  const svg = (
    <svg
      data-testid="rank-patch"
      data-tier={tier}
      data-tier-count={tierCount}
      data-glyph={blazon.glyph}
      data-pips={blazon.pips}
      role="img"
      aria-label={ariaLabel}
      width={px}
      height={px * ASPECT}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("shrink-0", className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={blazon.gradientFrom} />
          <stop offset="1" stopColor={blazon.gradientTo} />
        </linearGradient>
      </defs>

      {/* Shield body */}
      <path d={SHIELD_PATH} fill={`url(#${gradientId})`} />
      {/* Soft top-edge highlight */}
      <path
        d="M12 9 H60"
        stroke="#ffffff"
        strokeOpacity="0.3"
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Glyph — canonical lucide path data, white stroke, upper two-thirds */}
      <g
        transform="translate(21 14) scale(1.25)"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {GLYPH_PATHS[blazon.glyph].map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>

      {/* Climbing star pips — lower third (dropped at corner size) */}
      {showPips &&
        Array.from({ length: blazon.pips }).map((_, i) => (
          <polygon
            key={i}
            data-testid="rank-patch-pip"
            points={pipStarPoints(
              pipStartX + i * pipSpacing,
              pipCenterY,
              3.2,
              1.4,
            )}
            fill="#ffffff"
          />
        ))}
    </svg>
  )

  if (!showLabel) {
    return svg
  }

  return (
    <div className={cn("inline-flex flex-col items-center gap-1.5")}>
      {svg}
      <span
        data-testid="rank-patch-label"
        className="text-foreground max-w-[8rem] truncate text-sm font-medium"
      >
        {name}
      </span>
    </div>
  )
}
